import { Box } from "@mui/material";
import type { PopperProps } from "@mui/material/Popper";
import type { PopperChildrenProps } from "@mui/base/Popper";
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, MutableRefObject, ReactNode } from "react";

const LIST_GAP_PX = 4;

function resolveAnchorEl(
  anchorEl: PopperProps["anchorEl"],
): HTMLElement | null {
  if (anchorEl == null) return null;
  const el =
    typeof anchorEl === "function"
      ? (anchorEl as () => HTMLElement | null)()
      : (anchorEl as HTMLElement | null);
  if (
    el &&
    typeof el === "object" &&
    typeof (el as HTMLElement).getBoundingClientRect === "function"
  ) {
    return el as HTMLElement;
  }
  return null;
}

function resolvePopperChildren(
  children: PopperProps["children"],
): ReactNode {
  if (typeof children === "function") {
    return (children as (props: PopperChildrenProps) => ReactNode)({
      placement: "bottom-start",
    });
  }
  return children ?? null;
}

/**
 * Popper para MUI Autocomplete: portal no body, posição via getBoundingClientRect
 * (zoom do navegador) e largura igual à do campo.
 */
export const ViewportAnchoredAutocompletePopper = forwardRef<
  HTMLDivElement,
  PopperProps
>(function ViewportAnchoredAutocompletePopper(props, forwardedRef) {
  const { anchorEl, open, children, style, className } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const assignRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  const syncPosition = useCallback(() => {
    const root = rootRef.current;
    const anchor = resolveAnchorEl(anchorEl);
    if (!root || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = r.width;
    root.style.position = "fixed";
    root.style.left = `${r.left}px`;
    root.style.top = `${r.bottom + LIST_GAP_PX}px`;
    root.style.width = `${w}px`;
    root.style.maxWidth = `${w}px`;
    root.style.minWidth = `${w}px`;
    root.style.overflowX = "hidden";
    root.style.zIndex = "1300";
    root.style.boxSizing = "border-box";
    root.style.margin = "0";
    root.style.transform = "none";
  }, [anchorEl]);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
    const raf = requestAnimationFrame(syncPosition);
    const anchor = resolveAnchorEl(anchorEl);
    let ro: ResizeObserver | undefined;
    if (anchor && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => syncPosition());
      ro.observe(anchor);
    }
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    };
  }, [open, syncPosition, anchorEl]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const styleObj = (style ?? {}) as CSSProperties;
  const { width: _w, ...restStyle } = styleObj;

  return createPortal(
    <Box
      ref={assignRef}
      className={className}
      sx={{
        pointerEvents: "auto",
        zIndex: 1300,
        position: "fixed",
        boxSizing: "border-box",
        margin: 0,
        transform: "none",
        overflowX: "hidden",
        "& .MuiAutocomplete-paper": {
          width: "100% !important",
          maxWidth: "100% !important",
          minWidth: 0,
          boxSizing: "border-box",
        },
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          overflowX: "hidden",
        },
        "& .MuiAutocomplete-option": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
      style={{
        ...(restStyle as CSSProperties),
        position: "fixed",
        zIndex: 1300,
        pointerEvents: "auto",
      }}
    >
      {resolvePopperChildren(children)}
    </Box>,
    document.body,
  );
});
